import { useKeycloak } from '@react-keycloak/web';
import { ComponentType, useEffect, useState } from 'react';
import { Redirect, Route, useLocation, useRouteMatch } from 'react-router-dom';
import type { RouteComponentProps, RouteProps } from 'react-router-dom';
import { incrementPageCounter } from '../api/insights';
import { AdminLayout, NonAdminLayout } from '../components';
import { getConfig } from '../config/config';
import { RouteMappings } from '../constants/routes';
import { LoggedInUserContext } from '../context/loggedInUser';
import { useIsTenantAdmin } from '../hooks/useIsTenantAdmin';

interface PrivateRouteParams extends RouteProps {
  component: ComponentType<RouteComponentProps<any>> | ComponentType<any>;
}

export function PrivateRoute({
  component: Component,
  ...rest
}: PrivateRouteParams) {
  const { keycloak } = useKeycloak();
  const isTenantAdmin = useIsTenantAdmin();
  const location = useLocation();
  const [loggedInUser, setLoggedInUser] = useState<any>({});
  const [lastVisityPage, setLastVisitedPage] = useState<string | undefined>(
    undefined,
  );
  const match = useRouteMatch<Record<string, string>>({
    path: Object.keys(RouteMappings),
    exact: true,
  });
  useEffect(() => {
    if (match) {
      const routeName = RouteMappings[match.path];
      if (routeName !== lastVisityPage) {
        setLastVisitedPage(routeName);
        incrementPageCounter(routeName);
      }
    }
  }, [location, match, lastVisityPage]);
  useEffect(() => {
    if (keycloak.idToken) {
      setLoggedInUser(keycloak?.idTokenParsed);
    } else {
      keycloak.loadUserProfile().then((profile: any) => {
        setLoggedInUser(profile);
      });
    }
  }, [keycloak]);

  const wrapPage = (props: any) =>
    isTenantAdmin ? (
      <AdminLayout>
        <Component {...props} />
      </AdminLayout>
    ) : (
      <NonAdminLayout>
        <Component {...props} />
      </NonAdminLayout>
    );

  return (
    <Route
      {...rest}
      render={props =>
        keycloak?.authenticated ? (
          <LoggedInUserContext.Provider value={loggedInUser}>
            {getConfig().showNavigation?.toString()?.toLowerCase() ===
            'true' ? (
              wrapPage(props)
            ) : (
              <Component {...props} />
            )}
          </LoggedInUserContext.Provider>
        ) : (
          <Redirect
            to={{
              pathname: '/',
              state: { from: props.location },
            }}
          />
        )
      }
    />
  );
}
